import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { BLOCKS, BlockRenderer } from "@/blocks";
import { ArrowLeft, Save, Trash2, Plus, ChevronUp, ChevronDown, Eye, EyeOff, ExternalLink } from "lucide-react";

/**
 * PageEditor — left: block list, middle: block prop editor, right: live preview iframe/scaled.
 */
export default function PageEditor() {
  const { pageId } = useParams();
  const { activeTenant } = useTenant();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [selectedBlockIdx, setSelected] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedTs, setSavedTs] = useState(null);
  const [tab, setTab] = useState("blocks"); // blocks | seo | settings
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    api.get(`/pages/${pageId}`).then(({ data }) => setPage(data));
  }, [pageId]);

  const update = (patch) => setPage((p) => ({ ...p, ...patch }));
  const updateBlock = (idx, patch) => {
    const blocks = [...page.blocks];
    blocks[idx] = { ...blocks[idx], ...patch };
    update({ blocks });
  };
  const updateBlockProps = (idx, patch) => {
    const blocks = [...page.blocks];
    blocks[idx] = { ...blocks[idx], props: { ...blocks[idx].props, ...patch } };
    update({ blocks });
  };
  const move = (idx, dir) => {
    const blocks = [...page.blocks];
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return;
    [blocks[idx], blocks[j]] = [blocks[j], blocks[idx]];
    update({ blocks });
    setSelected(j);
  };
  const removeBlock = (idx) => {
    const blocks = page.blocks.filter((_, i) => i !== idx);
    update({ blocks });
    setSelected(Math.max(0, idx - 1));
  };
  const addBlock = (type) => {
    const def = BLOCKS[type];
    const newBlock = { id: `${type}-${Math.random().toString(36).slice(2, 8)}`, type, visible: true, props: { ...def.defaultProps } };
    update({ blocks: [...page.blocks, newBlock] });
    setSelected(page.blocks.length);
    setShowAdd(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/pages/${pageId}`, {
        title: page.title,
        slug: page.slug,
        page_type: page.page_type,
        status: page.status,
        nav_visible: page.nav_visible,
        blocks: page.blocks,
        seo: page.seo,
      });
      setSavedTs(new Date().toLocaleTimeString());
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this page?")) return;
    await api.delete(`/pages/${pageId}`);
    navigate("/admin/website/pages");
  };

  const selectedBlock = page?.blocks?.[selectedBlockIdx];
  const blockDef = selectedBlock ? BLOCKS[selectedBlock.type] : null;

  if (!page || !activeTenant) return <div className="p-10 text-[var(--n-text-muted)]">Loading…</div>;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)]" data-testid="page-editor">
      {/* Toolbar */}
      <div className="border-b border-[var(--n-border)] px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/admin/website/pages" className="text-[var(--n-text-sec)] hover:text-white" data-testid="editor-back">
            <ArrowLeft size={16} />
          </Link>
          <div className="min-w-0">
            <input
              value={page.title}
              onChange={(e) => update({ title: e.target.value })}
              className="bg-transparent font-cab font-black text-xl border-b border-transparent hover:border-[var(--n-border)] focus:border-[var(--n-blue)] outline-none"
              data-testid="page-title-input"
            />
            <div className="text-xs font-mono-plex text-[var(--n-text-muted)] mt-0.5">
              /site/{activeTenant.slug}{page.slug} · {page.status}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {savedTs && <span className="text-xs font-mono-plex text-[var(--n-text-muted)]">saved {savedTs}</span>}
          <a href={`/site/${activeTenant.slug}${page.slug === "/" ? "" : page.slug}`} target="_blank" rel="noreferrer" className="n-btn-ghost n-btn text-xs flex items-center gap-1" data-testid="preview-page-button">
            <ExternalLink size={12} /> PREVIEW
          </a>
          <button
            onClick={() => update({ status: page.status === "published" ? "draft" : "published" })}
            className="n-btn-ghost n-btn text-xs flex items-center gap-1"
            data-testid="publish-toggle"
          >
            {page.status === "published" ? <><Eye size={12}/>PUBLISHED</> : <><EyeOff size={12}/>DRAFT</>}
          </button>
          <button onClick={save} disabled={saving} className="n-btn-blue n-btn text-xs flex items-center gap-1" data-testid="save-page-button">
            <Save size={12} /> {saving ? "SAVING…" : "SAVE"}
          </button>
          <button onClick={del} className="text-[var(--n-danger)] text-xs flex items-center gap-1 ml-2" data-testid="delete-page-button">
            <Trash2 size={12} /> delete
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[var(--n-border)] px-6">
        {["blocks", "seo", "settings"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-xs font-mono-plex uppercase tracking-wider border-b-2 -mb-px ${tab === t ? "border-white text-white" : "border-transparent text-[var(--n-text-sec)]"}`}
            data-testid={`editor-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 grid grid-cols-12 min-h-0">
        {/* Left — block list */}
        <aside className="col-span-3 border-r border-[var(--n-border)] overflow-auto p-3" data-testid="editor-block-list">
          {tab === "blocks" ? (
            <>
              {page.blocks.map((b, i) => {
                const def = BLOCKS[b.type];
                return (
                  <div
                    key={b.id}
                    onClick={() => setSelected(i)}
                    className={`p-2 mb-1 cursor-pointer border ${selectedBlockIdx === i ? "border-[var(--n-blue)] bg-[#0a1530]" : "border-[var(--n-border)] hover:bg-[#101015]"}`}
                    data-testid={`block-item-${i}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <div className="font-cab font-bold text-xs truncate">{def?.label || b.type}</div>
                        <div className="text-[10px] font-mono-plex text-[var(--n-text-muted)] truncate">{b.props?.headline || b.props?.title || ""}</div>
                      </div>
                      <div className="flex gap-0.5">
                        <button onClick={(e) => { e.stopPropagation(); move(i, -1); }} className="p-0.5 hover:bg-[#1a1a1a]"><ChevronUp size={11}/></button>
                        <button onClick={(e) => { e.stopPropagation(); move(i, 1); }} className="p-0.5 hover:bg-[#1a1a1a]"><ChevronDown size={11}/></button>
                        <button onClick={(e) => { e.stopPropagation(); updateBlock(i, { visible: !b.visible }); }} className="p-0.5 hover:bg-[#1a1a1a]" title="toggle">
                          {b.visible === false ? <EyeOff size={11}/> : <Eye size={11}/>}
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); removeBlock(i); }} className="p-0.5 hover:bg-[#1a1a1a] text-[var(--n-danger)]"><Trash2 size={11}/></button>
                      </div>
                    </div>
                  </div>
                );
              })}
              <button onClick={() => setShowAdd(true)} className="w-full mt-2 border border-dashed border-[var(--n-border)] p-3 text-xs font-cab font-medium text-[var(--n-text-sec)] hover:bg-[#101015]" data-testid="add-block-button">
                <Plus size={12} className="inline mr-1"/> ADD BLOCK
              </button>
            </>
          ) : tab === "seo" ? (
            <div className="text-xs font-mono-plex text-[var(--n-text-muted)] p-2">Edit SEO in the right panel.</div>
          ) : (
            <div className="text-xs font-mono-plex text-[var(--n-text-muted)] p-2">Edit page settings on the right.</div>
          )}
        </aside>

        {/* Center — editor form */}
        <section className="col-span-4 border-r border-[var(--n-border)] overflow-auto p-6" data-testid="editor-form">
          {tab === "blocks" && selectedBlock && blockDef && (
            <>
              <div className="n-label mb-1">{blockDef.label}</div>
              <div className="font-cab font-black text-2xl tracking-tight mb-4">Edit block</div>
              {blockDef.schema.map((f) => (
                <FieldEditor
                  key={f.key}
                  field={f}
                  value={selectedBlock.props?.[f.key]}
                  onChange={(v) => updateBlockProps(selectedBlockIdx, { [f.key]: v })}
                />
              ))}
            </>
          )}
          {tab === "seo" && (
            <div className="space-y-3">
              <div className="font-cab font-black text-2xl tracking-tight mb-2">SEO</div>
              {[
                ["title", "Title"], ["description", "Description"], ["keywords", "Keywords"],
                ["og_image", "OG image URL"], ["canonical", "Canonical URL"],
              ].map(([k, l]) => (
                <div key={k}>
                  <label className="n-label block mb-1">{l}</label>
                  {k === "description" ? (
                    <textarea rows={3} className="n-input" value={page.seo?.[k] || ""} onChange={(e) => update({ seo: { ...page.seo, [k]: e.target.value } })} data-testid={`seo-${k}`} />
                  ) : (
                    <input className="n-input" value={page.seo?.[k] || ""} onChange={(e) => update({ seo: { ...page.seo, [k]: e.target.value } })} data-testid={`seo-${k}`} />
                  )}
                </div>
              ))}
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={!!page.seo?.noindex} onChange={(e) => update({ seo: { ...page.seo, noindex: e.target.checked } })} data-testid="seo-noindex" />
                <span className="text-xs font-mono-plex uppercase text-[var(--n-text-sec)]">noindex (exclude from sitemap)</span>
              </label>
            </div>
          )}
          {tab === "settings" && (
            <div className="space-y-3">
              <div className="font-cab font-black text-2xl tracking-tight mb-2">Settings</div>
              <div>
                <label className="n-label block mb-1">Path</label>
                <input className="n-input font-mono-plex" value={page.slug} onChange={(e) => update({ slug: e.target.value })} data-testid="settings-slug" />
              </div>
              <div>
                <label className="n-label block mb-1">Type</label>
                <select className="n-input" value={page.page_type} onChange={(e) => update({ page_type: e.target.value })}>
                  {["home", "about", "services", "service_detail", "city", "faq", "reviews", "gallery", "blog", "contact", "legal", "not_found", "custom"].map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 mt-2">
                <input type="checkbox" checked={!!page.nav_visible} onChange={(e) => update({ nav_visible: e.target.checked })} data-testid="settings-nav-visible" />
                <span className="text-xs font-mono-plex uppercase text-[var(--n-text-sec)]">Show in navigation</span>
              </label>
            </div>
          )}
        </section>

        {/* Right — live preview */}
        <section className="col-span-5 overflow-auto bg-[#0a0a0a]" data-testid="editor-preview">
          <div className="p-4">
            <div className="n-label mb-2">Live preview</div>
            <div className="border border-[var(--n-border)] overflow-hidden bg-white rounded">
              <div className="tenant-site-theme" style={{ transform: "scale(0.6)", transformOrigin: "top left", width: "166%", pointerEvents: "none" }}>
                {page.blocks.map((b) => (
                  <BlockRenderer key={b.id} block={b} tenant={activeTenant} site={{}} />
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Add-block modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setShowAdd(false)}>
          <div className="n-card w-full max-w-2xl p-6 max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="add-block-modal">
            <div className="font-cab font-black text-2xl mb-4">Add a block</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(BLOCKS).map(([type, def]) => (
                <button
                  key={type}
                  onClick={() => addBlock(type)}
                  className="text-left p-3 border border-[var(--n-border)] hover:bg-[#101015]"
                  data-testid={`add-block-${type}`}
                >
                  <div className="font-cab font-bold text-sm">{def.label}</div>
                  <div className="text-[10px] font-mono-plex text-[var(--n-text-muted)]">{type}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------- Field editor sub-component --------
function FieldEditor({ field, value, onChange }) {
  const { type, key, label, options, itemSchema, rows } = field;
  if (type === "textarea" || type === "url") {
    return (
      <div className="mb-3">
        <label className="n-label block mb-1">{label}</label>
        {type === "textarea"
          ? <textarea rows={rows || 4} className="n-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${key}`} />
          : <input className="n-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${key}`} />}
      </div>
    );
  }
  if (type === "textarea_lines") {
    return (
      <div className="mb-3">
        <label className="n-label block mb-1">{label}</label>
        <textarea rows={5} className="n-input" value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n").filter(Boolean))} data-testid={`field-${key}`} />
      </div>
    );
  }
  if (type === "select") {
    return (
      <div className="mb-3">
        <label className="n-label block mb-1">{label}</label>
        <select className="n-input" value={value || options[0]} onChange={(e) => onChange(e.target.value)} data-testid={`field-${key}`}>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );
  }
  if (type === "items") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="mb-4">
        <label className="n-label block mb-2">{label}</label>
        {arr.map((item, idx) => (
          <div key={idx} className="border border-[var(--n-border)] p-3 mb-2 relative" data-testid={`item-${key}-${idx}`}>
            <button
              type="button"
              onClick={() => onChange(arr.filter((_, i) => i !== idx))}
              className="absolute top-1 right-1 text-[var(--n-danger)] text-xs"
              data-testid={`item-remove-${key}-${idx}`}
            ><Trash2 size={11}/></button>
            {itemSchema.map((sub) => (
              <div key={sub.key} className="mb-2">
                <label className="text-[10px] font-mono-plex uppercase text-[var(--n-text-muted)] block mb-1">{sub.label}</label>
                {sub.type === "textarea"
                  ? <textarea rows={2} className="n-input text-xs" value={item[sub.key] || ""} onChange={(e) => onChange(arr.map((it, i) => i === idx ? { ...it, [sub.key]: e.target.value } : it))} />
                  : sub.type === "select"
                    ? <select className="n-input text-xs" value={item[sub.key] || sub.options[0]} onChange={(e) => onChange(arr.map((it, i) => i === idx ? { ...it, [sub.key]: e.target.value } : it))}>{sub.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                    : <input className="n-input text-xs" value={item[sub.key] || ""} onChange={(e) => onChange(arr.map((it, i) => i === idx ? { ...it, [sub.key]: e.target.value } : it))} />}
              </div>
            ))}
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            const empty = Object.fromEntries(itemSchema.map((s) => [s.key, ""]));
            onChange([...arr, empty]);
          }}
          className="w-full border border-dashed border-[var(--n-border)] p-2 text-xs font-cab font-medium text-[var(--n-text-sec)] hover:bg-[#101015]"
          data-testid={`item-add-${key}`}
        >
          <Plus size={11} className="inline mr-1"/> ADD ITEM
        </button>
      </div>
    );
  }
  // default text
  return (
    <div className="mb-3">
      <label className="n-label block mb-1">{label}</label>
      <input className="n-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${key}`} />
    </div>
  );
}
